import {IViewViewModelConstructorParameter} from '#/CoreFrameInterfaces';
import ViewViewModel from '@/ViewModels/ViewViewModel';
import {ILoginDTO} from "coreframetypings/dto"
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import TextBoxViewModelT from '@/ViewModels/Controls/TextBoxViewModel';
import PasswordBoxViewModelT from '@/ViewModels/Controls/PasswordBoxViewModel';
import CheckBoxViewModelT from '@/ViewModels/Controls/CheckBoxViewModel';
import DropDownListViewModelT from '@/ViewModels/Controls/DropDownListViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import NavigationService from '@/Management/NavigationService';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import StorageHelper from '@/Utility/StorageHelper';
import Loader from '@/Utility/Loader';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import TwoFactorTokenDialogViewFactory from '@/Components/TwoFaktorAuth/TokenDialog/TwoFactorTokenDialogViewFactory';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';

declare let moment: moment.MomentStatic;

interface ILoginViewViewModelConstructorParameter extends IViewViewModelConstructorParameter {
    showError: KnockoutObservable<boolean>;
}

export default class LoginViewModel extends ViewViewModel {
    
    /**
     * Gibt an, ob ein Fehler angezeigt werden soll
     */
    public showError: KnockoutObservable<boolean>;
    
    /**
     * Die Fehlermeldung falls bei der Anmeldung ein Fehler auftritt
     */
    public errorMessage: LabelViewModelT;
    
    /**
     * Die Fehlermeldung falls eine Systemsperre aktiv ist
     */
    public errorLockedMessage: LabelViewModelT;

    /**
     * Die Begründung, warum das System aktuell gesperrt ist
     */
    public errorLockedInfo: LabelViewModelT;
    
    /**
     * Das Eingabefeld für den Benutzernamen
     */
    public username: TextBoxViewModelT;

    /**
     * Der Eingabewert für das Passwort
     */
    public passwordOBS: KnockoutObservable<string>;

    /**
     * Die Passwort-Box
     */
    public password: PasswordBoxViewModelT
    
    /**
     * Das Label für den Mandanten
     */
    public principalLabel: LabelViewModelT;
    
    /**
     * Die Auswahlbox für den Mandanten
     */
    public principal: DropDownListViewModelT;
    
    /**
     * Die Auswahlbox für die Sprache
     */
    public language: DropDownListViewModelT;

    /**
     * Gibt an, ob ein Login gerade möglich ist
     */
    public isLoginEnabled: KnockoutObservable<boolean>;
    
    /**
     * Der Button zum Starten des Anmeldevorgangs
     */
    public button: ButtonViewModelT;

    /*
    * Das Label für den Trennstrich
    */
    public ssoLoginSeparatorLabel: LabelViewModelT;

    /**
     * Enthält Informationen über aktive IdentityProvider
     */
    public identityProviders: Array<IdentityProvider>;

    /**
     * Die ViewModel der aktiven IDP-Buttons
     */
    public identityProviderButtonViewModels: Array<ButtonViewModelT>;

    /**
     * Gibt an, ob ebenfalls ein Login über Username/Passwort-Kombi möglich ist
     */
    public basicAuthenticationIsEnabled: KnockoutObservable<boolean>;

    /**
     * Observable um die Login-Inputs ein/auszublenden
     */
    public showLoginInputs: KnockoutObservable<boolean>;

    /**
     * Observable um das Anwendungslogo ein/auszublenden
     */
    public showCustomerLogo: KnockoutObservable<boolean>;

    /**
     * Observable um die 2FA-Setup-View ein/auszublenden
     */
    public showInitialConfiguration: KnockoutObservable<boolean>;

    /**
     * Observable um die 2FA-Eingabe ein/auszublenden
     */
    public showTokenValidation: KnockoutObservable<boolean>;
    
    public useSsoCheckBox: CheckBoxViewModelT;

    public customerLogo: ImageViewModelT;
    public buildVersion: LabelViewModelT;
    public tokenDialogViewFactory: TwoFactorTokenDialogViewFactory;
    public applicationIdentifierLabel: LabelViewModel;

    private readonly AutoSelect: string;
    private readonly userLoginLanguage: KnockoutObservable<string>;
    private readonly canDisableSSO: boolean;
    private readonly isUseSsoChecked: KnockoutObservable<boolean>;
    private readonly isWindowsAuth: boolean;
    
    private LabelViewModel= ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelT>('TextBoxViewModel');
    private PasswordBoxViewModel = ComponentManager.getViewModel<typeof PasswordBoxViewModelT>('PasswordBoxViewModel');
    private CheckBoxViewModel= ComponentManager.getViewModel<typeof CheckBoxViewModelT>('CheckBoxViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelT>('DropDownListViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private _timer: any;

    constructor(params: ILoginViewViewModelConstructorParameter) {
        super(params);
        this.AutoSelect = '<AUTO_SELECT>';
        this.applicationIdentifierLabel = this.createApplicationIdentifierLabelViewModel()

        this.showInitialConfiguration = this.observableOrDefault(false);
        this.showTokenValidation = this.observableOrDefault(false);
        
        this.customerLogo = this.createCustomerLogoViewModel();

        this.basicAuthenticationIsEnabled = this.observableOrDefault(MODEL.BasicAuthenticationIsEnabled ?? true);
        
        this.identityProviders = (MODEL.SSOConnectors || []);
        this.canDisableSSO = MODEL.CanDisableSSO ?? false;
        this.isWindowsAuth = MODEL.IsWindowsAuth ?? false;
        
        this.userLoginLanguage = this.observableOrDefault(StorageHelper.getGlobal('lastLoggedInLanguage'), 'EN');
        
        this._timer = null; 

        // Mit TryAutoLogin (z.B. nach STRG+Click) wird die Login-View erst einmal nicht angezeigt,
        // da sie in der Regel nur für weniger als eine Sekunde sichtbar ist, wenn der Login funktioniert.
        const autoLogin = MODEL.TryAutoLogin ?? false;
        const ssoLogin = MODEL.TrySSOLogin ?? false;
        
        this.showLoginInputs = this.observableOrDefault(!autoLogin && !ssoLogin);
        this.showCustomerLogo = this.observableOrDefault(!autoLogin && !ssoLogin);
        
        this.showError = this.observableOrDefault(params.showError, RADApplication.getInstance().isSystemLocked());

        this.errorMessage = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'error-message',
            isVisible: this.showError,
            ergName: ''
        });

        this.errorLockedMessage = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'locked-message',
            isVisible: RADApplication.getInstance().isSystemLocked,
            ergName: 'SYSTEM_LOCK_MESSAGE_HEADER'
        });

        this.errorLockedInfo = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'locked-message-info',
            isVisible: RADApplication.getInstance().isSystemLocked,
            caption: RADApplication.getInstance().systemLockMessage()
        });
        
        this.isUseSsoChecked = this.observableOrDefault(true);
        this.useSsoCheckBox = new CheckBoxViewModelT({
            isChecked: this.isUseSsoChecked,
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'sso.label',
                ergName: 'SSO_ACTIVE'
            }),
            isVisible: this.canDisableSSO
        });

        // Wenn SSO deaktiviert wird, muss SSO im User deaktiviert werden
        this.defineSubscription(this.isUseSsoChecked, (useSso) => {
            const app = RADApplication.getInstance();
            const isSingleSignOn = useSso && !!app.ssoUserName;
            app.user.isSingleSignOn(isSingleSignOn);
        });

        // Wenn SSO im User aktiv ist, muss der Username auf den SSO-Username gesetzt werden
        // Der Code wäre in User wahrscheinlich besser aufgehoben
        this.defineSubscription(RADApplication.getInstance().user.isSingleSignOn, (isSSO) => {
            if (!isSSO)
                return;
            const app = RADApplication.getInstance();
            app.user.username(app.ssoUserName);
        })(RADApplication.getInstance().user.isSingleSignOn());
        
        const stateComputed = this.defineComputed(() => {
            const isBasicAuthenticationEnabled = this.basicAuthenticationIsEnabled();
            const isCheckBoxChecked = this.useSsoCheckBox.isChecked();

            if (!isBasicAuthenticationEnabled)
                return false;

            if (this.isWindowsAuth) {
                return !isCheckBoxChecked;
            }

            return true;
        });
        
        this.username = new this.TextBoxViewModel({
            parentLocator: this.locator,
            locator: 'username',
            isEnabled: stateComputed,
            name: 'username',
            isFocusable: true,
            onSubmit: () => {
                // gleicher Call, wie das click-Event des Login-Buttons
                this.doLogin(false, null);
            },
            value: RADApplication.getInstance().user.username,
            placeholder: ResourceManager.getInstance().get('USER_NAME'),
        });

        this.passwordOBS = this.observableOrDefault('');

        this.password = new this.PasswordBoxViewModel({
            parentLocator: this.locator,
            locator: 'password',
            value: this.passwordOBS,
            name: 'password',
            isFocusable: true,
            isEnabled: MODEL.BasicAuthenticationIsEnabled || (this.isWindowsAuth && this.canDisableSSO),
            autoFillEnabled: true,
            placeholder: ResourceManager.getInstance().get('USER_PASSWORD'),
            showPasswordIcon: true,
            onSubmit: () => {
                // gleicher Call, wie das click-Event des Login-Buttons
                this.doLogin(false, null);
            },
            isVisible: stateComputed
        });

        this.principalLabel = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'principal.label',
            ergName: 'PRINCIPAL_NAME'
        });

        const principalDropDown = new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'principal',
            isFocusable: true,
            value: RADApplication.getInstance().user.principal,
            data: RADApplication.getInstance().user.principalList,
            dataTextField: 'name',
            dataValueField: 'name',
            template: '# if (data) { #<span>#:data.name#</span># } #',
            valueTemplate: '# if (data) { #<span>#:data.name#</span># } #',
            ariaLabel: ResourceManager.getInstance().get('PRINCIPAL_NAME')
        });
        
        // Dürfen nicht beim verlassen der LoginView disposed werden, da sonst 
        // die Subscripitions auf den Observables entfernt werden.
        principalDropDown.undisposeProperties.push('value');
        principalDropDown.undisposeProperties.push('data');
        this.principal = principalDropDown;

        this.language = new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'language',
            isFocusable: true,
            value: this.defineComputed({
                read: () => {
                        return ResourceManager.getInstance().language.value();
                },
                write: (value) => {
                    ResourceManager.getInstance().language.value(value);
                    this.updateIdentityProviderCaptions()
                }
            }),
            data: ResourceManager.getInstance().getLanguagesForLogin(),
            dataTextField: 'displayName',
            dataValueField: 'iso6391',
            // Flaggen-Icon dient zu "Dekorationszwecken" und muss daher nicht noch mal doppelt beschrieben werden
            // Es würde sonst ein Screenreaader bswp. "deutsche Flagge Deutsch" vorlesen, daher kann der alt="" gesetzt werden
            template: '# if (data && data.iconUrl) { #<img src=\"#:data.iconUrl#\" alt="" height=\"16\" /><span>#:data.displayName#</span># } #',
            valueTemplate: '# if (data && data.iconUrl) { #<img src=\"#:data.iconUrl#\" alt="" height=\"16\" /><span>#:data.displayName#</span># } #',
            listContainerName: 'language-selector-container',
            ariaLabel: ResourceManager.getInstance().get('MENU_LANGUAGE_NODE_NAME')
        });

        this.isLoginEnabled = this.observableOrDefault(true);
        this.button = new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'login',
            isPrimaryButton: true,
            isFocusable: true,
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'login.label',
                ergName: 'DO_LOGIN'
            }),
            onClick: () => {
                this.doLogin(false, null);
            },
            isEnabled: this.isLoginEnabled,
            isVisible: this.defineComputed(() => {
                // wenn basic Auth = true, dann muss der Button angezeigt werden
                if (this.basicAuthenticationIsEnabled())
                    return true;
                
                return this.identityProviders.length === 0 && !this.basicAuthenticationIsEnabled;
            })
        });

        this.ssoLoginSeparatorLabel = new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'ssoLoginSeparator.label',
            ergName: 'OR',
            toLowerCase: true,
        });

        this.buildVersion = new this.LabelViewModel({
            caption: RADApplication.getInstance().buildVersion,
            parentLocator: this.locator,
            locator: 'buildVersion',
        });

        /*
         * Registrert ein KeyPress-Event um "Enter", "ESC" während des Logins zu unterstützen
         */
        (() => {
            this._timer = this.async(() => {
                NavigationService.getInstance().reload();
            }, 1000 * 60 * 20);
        })();

        this.tokenDialogViewFactory = null;
        // https://gist.github.com/jonleighton/958841
        (() => {
            if (MODEL.TryAutoLogin) { // Szenario: STRG+Click
                Loader.getInstance().show();
                RADEnvironmentProxy.getInstance().tryAutoLogin({
                    principal: MODEL.PrincipalName
                }).then((loginDTO) => {
                    // wird ein AutoLogin versucht, aber es ist eine Session via IDP vorhanden, so versuchen wir den Login nochmal via SSO
                    const successful = this.handleLogin(loginDTO);
                    if (!successful && this.isSSOLoginPossible())
                        this.trySSOLogin();
                }).always(() => {
                    Loader.getInstance().hide();
                });
            } else if (this.isSSOLoginPossible()) { // Szenario: Automatischer SSO Login
                this.trySSOLogin();
            } else { // Szenario: Login-Maske
                if (MODEL.PrincipalName)
                    RADApplication.getInstance().user.principal(MODEL.PrincipalName);
            }
        })();

        this.defineSubscription(RADApplication.getInstance().user.needsTwoFaRegistration, (userNeedRegistration) => {
            if (userNeedRegistration) {
                this.showLoginInputs(false);
                this.showInitialConfiguration(true);
            }
        });
        
        this.identityProviderButtonViewModels = this.createIdentityProviderButtons();
    }
    
    private trySSOLogin() {
        const forceAutoSelect = !MODEL.DeactivateAutoSelect;
        const forcedPrincipalName = forceAutoSelect ? null : MODEL.PrincipalName;
        this.doLogin(forceAutoSelect, forcedPrincipalName, true);
    }
    
    public doLogin(forceAutoSelect?: boolean, forcedPrincipal?: string, isAutoLogin: boolean = false, tokenExplicitlyAdded: boolean = false): JQuery.Promise<boolean> {
        this.showError(false);
        let username = this.username.value();
        let password = this.passwordOBS();

        let principal;
        if (forcedPrincipal) {
            principal = forcedPrincipal;
        } else {
            let autoselectPrincipal = RADApplication.getInstance().isSystemParamSetAndTrue('Login.AutoselectPrincipal');
            if (autoselectPrincipal || forceAutoSelect) {
                principal = this.AutoSelect;
            } else {
                principal = this.principal.value();
            }
        }
        
        let language = this.userLoginLanguage();
        
        StorageHelper.bind('userLanguage', language);
        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowChangeLanguage')) {
            language = this.language.value();
        }
            
        Loader.getInstance().show();
        /*
         * Versuch, den Benutzer über den Proxy anzumelden
         * Im Fehlerfall wird eine Meldung angezeigt, ansonsten wird zur aufgerufenen Seite navigiert
         */
        return RADEnvironmentProxy.getInstance().loginEncodedWithLanguage({
            username: username,
            password: this.encrypt(password),
            principal: principal,
            remainSignedIn: true, // Hat keinen Effekt mehr
            language: language,
            tokenExplicitlyAdded: tokenExplicitlyAdded
        }).then((loginDTO) => {
            return this.handleLogin(loginDTO);
        }).always(() => {
            Loader.getInstance().hide();
        });
    }
    
    public handleLogin(loginDTO: ILoginDTO): boolean {
        if (loginDTO.isAuthorized) {
            RADApplication.getInstance().menuSpriteLoaded = $.Deferred();
            RADEnvironmentProxy.getInstance().getMenuImagesSprite().then((menuSpriteDTO) => {
                RADApplication.getInstance().menuSprite = menuSpriteDTO;
                RADApplication.getInstance().menuSpriteLoaded.resolve(true);
            });
            let clientInfo = loginDTO.clientInfo;
            let principal = clientInfo && clientInfo.user
                ? clientInfo.user.principal
                : null;
            // Bei this.AutoSelect muss der Principal vom Server gesetzt wurde
            if (principal === this.AutoSelect)
                principal = loginDTO.principal;
            // Wenn kein Principal gesetzt ist, nimm den Principal aus dem User
            if (principal == null)
                principal = RADApplication.getInstance().user.principal();
            let principalDTO = _.find(loginDTO.assignedPrincipals, (p) => {
                return p.name == principal;
            });
            RADApplication.getInstance().afterLogin(loginDTO, principalDTO);
            if (this._timer != null) {
                window.clearTimeout(this._timer);
            }
            if (!loginDTO.clientInfo.user.needsTwoFaRegistration && !this.isDisposed) {
                this.passwordOBS('');
                this.dispose();
            }
            return true;
        } else {
            if (loginDTO.twoFATokenNeeded) {
                this.openTwoFATokenDialog();
                return false;
            }
            this.passwordOBS('');
            
            // schlägt bspw ein Auto-Login fehl, so muss sichergestellt sein, dass die Eingabemaske wieder sichtbar gemacht wird, und ein
            // erneuter Login stattfinden kann - aber nur, wenn man nicht im SSO-Kontext unterwegs ist!
            if (!this.isSSOLoginPossible()) {
               this.showRequiredLoginComponents();
            }
            
            // Im Fehlerfall: Blocked o. User konnte nicht gefunden werden müssen die Komponenten wieder sichtbar gemacht werden
            this.showError(true);
            if (loginDTO.blockedForSeconds) {
                let seconds = loginDTO.blockedForSeconds;
                if (seconds > 0) {
                    this.isLoginEnabled(false);
                    this.showLoginBlockedMessage(seconds);
                    // Im Fehlerfall Blocked müssen die Komponenten wieder sichtbar gemacht werden
                    this.showRequiredLoginComponents();
                    let blockedInterval = setInterval(() => {
                        let delay = --seconds
                        if (delay <= 0) { // Delay is finished
                            clearInterval(blockedInterval);
                            this.showError(false);
                            this.isLoginEnabled(true);
                        } else {
                            this.showLoginBlockedMessage(delay);
                        }
                    }, 1000)
                }
            } else {
                // Im Fehlerfall bspw. User konnte nicht gefunden müssen die Komponenten wieder sichtbar gemacht werden
                if (loginDTO.resultErgName) {
                    this.errorMessage.ergName(loginDTO.resultErgName);
                    this.showRequiredLoginComponents();
                }
            }
            
            return false;
        }
    }
    
    private showRequiredLoginComponents() {
        this.showLoginInputs(true);
        this.showCustomerLogo(true);
    }
    
    private isSSOLoginPossible(): boolean {
        return RADApplication.getInstance().user.isSingleSignOn() && MODEL.SSOUserName && MODEL.TrySSOLogin;
    }

    protected disposeInternal(): void {
        if (this.tokenDialogViewFactory != null) {
            this.tokenDialogViewFactory.dispose();
            this.tokenDialogViewFactory = null;
        }

        // Sonderfall: Eigentlich sollte ein ViewModel nie seine Factory disposen müssen.
        // Da das LoginViewModel sich bei erfolgreichem Login selbst disposed, würden
        // Factory inkl. View aber bestehen bleiben. Deshalb wir räumen diese deshalb hiermit ab.
        if (this.viewFactory != null) {
            this.viewFactory.dispose();
            this.viewFactory = null;
        }

        super.disposeInternal();
    }

    private showLoginBlockedMessage(delay: any): any {
        if (this.errorMessage.ergName() !== null)
            this.errorMessage.ergName(null);
        let message = ResourceManager.getInstance().get('LOGIN_BLOCKED');
        if (message)
            message = message.replace('$$seconds$$', delay);
        this.errorMessage.caption(message);
    }

    public getViewBindingFor(view: string): {
        'uiGenerator': string,
        'viewModelParameters': any
    } {
        if (view === 'TwoFactorInitialConfig') {
            return {
                uiGenerator: 'TwoFactorInitialConfig',
                viewModelParameters: {
                    username: this.username.value(),
                    interactionCallback: () => this.onTwoFaConfigured()
                }
            };
        } else if (view === 'TokenValidationView') {
            return {
                uiGenerator: 'TokenValidationView',
                viewModelParameters: {
                    username: this.username.value(),
                    onSubmit: (token: string) => this.onSubmitTokenValidation(token),
                    onCancel: () => this.onCancelTwoFactorTokenValidation()
                }
            };
        }

        return {
            uiGenerator: view,
            viewModelParameters: {
                username: this.username.value(),
                onSubmit: (token: string) => this.onSubmitTokenValidation(token),
                onCancel: () => this.onCancelTwoFactorTokenValidation()
            }
        };
    }
    
    private onTwoFaConfigured() {
        RADEnvironmentProxy.getInstance().logout().then(() => {
           RADApplication.getInstance().afterLogout();
        });
    }

    private onSubmitTokenValidation(token: string) {
        this.showTokenValidation(false);
        this.showLoginInputs(true);
        let deferred = $.Deferred();
        this.passwordOBS(this.passwordOBS() + token);
        this.doLogin(false, null, false, true).then(success => {
            deferred.resolve(success);
        });
        return deferred.promise();
    }

    private onCancelTwoFactorTokenValidation() {
        this.tokenDialogViewFactory = null;
        if (this.isDisposed) {
            return;
        }
        this.passwordOBS('');
        this.showTokenValidation(false);
        this.showLoginInputs(true);
        this.showError(true);
    }

    private openTwoFATokenDialog(): any {
        this.showLoginInputs(false);
        this.showTokenValidation(true)
    }
    
    private createIdentityProviderButtons(): Array<ButtonViewModelT> {
        return this.identityProviders.map(provider => {
            return new ButtonViewModelBuilder()
                .withLabel()
                .withCaption(this.determineProviderLabelCaption(provider.DisplayName))
                .done()
                .withBackgroundColor(provider.BackgroundColor)
                .withBorderColor(provider.BorderColor)
                .withTextColor(provider.TextColor)
                .withHoverColor(provider.HoverColor)
                .withImage()
                .withSource(provider.IconUrl)
                .done()
                .withIsEnabled(this.isLoginEnabled)
                .withOnClick(() => {
                    const url = provider.Url;
                    window.open(url, '_self');
                }).build();
        });
    }
    
    private updateIdentityProviderCaptions(): void {
        if (!this.identityProviderButtonViewModels || this.identityProviderButtonViewModels.length === 0)
            return;
        
        const newLabels = this.identityProviders.map(idp => this.determineProviderLabelCaption(idp.DisplayName));
        this.identityProviderButtonViewModels.forEach((button: ButtonViewModelT, idx: number) => button.label.caption(newLabels[idx]));
    }
    
    private determineProviderLabelCaption(displayName: string): string {
        // Wert liegt nicht in mehreren Sprachen vor
        if (!(displayName.indexOf(ResourceManager.getInstance().MLSTRING_SEPARATOR) >= 0))
            return displayName;

        // gewinnt die Browsersprache? - nimm das Item (wenn vorhanden) für die Sprache des Browsers. Als Fallback wird EN verwendet (wenn vorhanden) ansonsten immer DE
        // andernfalls: Nimm die selektierte Sprache aus dem Dropdown
        // Sollte gar nichts passen, so wird ein Fehlertext im Button angezeigt
        let language = this.userLoginLanguage();
        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowChangeLanguage')) {
            language = this.language.value();
        }
        
        const langs = [language, 'EN', 'DE'];
        for (const lang of langs) {
            const value = ResourceManager.getInstance().getTranslationInMLString(displayName, lang);
            if (value)
                return value;
        }
        
        return '<UNKNOWN>';
    }

    /**
     * Verschlüsselung des Passworts
     */
    private encrypt(r: any): any {
        let utf8 = unescape(encodeURIComponent(r));
        let invBytes = [];
        for (let i = 0; i < utf8.length; i++) {
            invBytes.push(~utf8.charCodeAt(i));
        }
        let c = this.base64_encode(invBytes);
        return c;
    }

    private base64_encode(arrayBuffer: any): any {
        let base64 = '';
        let encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
        let bytes = new Uint8Array(arrayBuffer);
        let byteLength = bytes.byteLength;
        let byteRemainder = byteLength % 3;
        let mainLength = byteLength - byteRemainder;
        let a, b, c, d;
        let chunk;
        // Main loop deals with bytes in chunks of 3
        for (let i = 0; i < mainLength; i = i + 3) {
            // Combine the three bytes into a single integer
            chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
            // Use bitmasks to extract 6-bit segments from the triplet
            a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
            b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
            c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
            d = chunk & 63;               // 63       = 2^6 - 1
            // Convert the raw binary segments to the appropriate ASCII encoding
            base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
        }
        // Deal with the remaining bytes and padding
        if (byteRemainder == 1) {
            chunk = bytes[mainLength];
            a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2
            // Set the 4 least significant bits to zero
            b = (chunk & 3) << 4; // 3   = 2^2 - 1
            base64 += encodings[a] + encodings[b] + '==';
        } else if (byteRemainder == 2) {
            chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];
            a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
            b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4
            // Set the 2 least significant bits to zero
            c = (chunk & 15) << 2; // 15    = 2^4 - 1
            base64 += encodings[a] + encodings[b] + encodings[c] + '=';
        }
        return base64;
    }

    private createApplicationIdentifierLabelViewModel(): LabelViewModelT {
        return new LabelViewModelBuilder()
            .withErgName('LOGIN_TITLE_APPNAME')
            .withSimpleReplacement('appName', RADApplication.getInstance().appName())
            .build()
    }
    
    private createCustomerLogoViewModel(): ImageViewModelT {
        return new ImageViewModelBuilder()
            .withSource('Company_Watermark.svg')
            .withAlternativeTextErgName('ARIA_LOGIN_CUSTOMER_LOGO_DESC')
            .build()
    }
}

ComponentManager.setViewModel('LoginViewModel', LoginViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');